// ---------------------------------------------------------------------------
// The partner portal for External API (EPS) partners — the browser half.
// Asana 1219738994585570. The hub half is VTHub api/routes/epsPortal.js.
//
// An API partner signs in at /eps-login with its email; the hub mails a one-time link
// back to /eps-login?code=…, which is exchanged for a portal session (12 hours) kept
// under EPS_SESSION_KEY. That session opens the /eps-* pages ONLY, and the hub accepts
// it on its /eps-portal/* routes only. It is sent as a plain Bearer header, so the hub
// session transport (hubSession.js) never touches it.
//
// No imports on purpose: scripts/test-eps-portal.mjs runs it with plain node.
// ---------------------------------------------------------------------------

export const EPS_SESSION_KEY = 'epsPortalSession'
export const EPS_PATHS = ['/eps-login', '/eps-account', '/eps-listings', '/eps-bookings']
export const EPS_HOME = '/eps-account'
export const EPS_LOGIN = '/eps-login'

const store = () => (typeof localStorage === 'undefined' ? null : localStorage)

/** The stored session ({ token, expiresAt, partnerId, partnerName }) while unexpired, else null. */
export const getEpsSession = (now = Date.now()) => {
    try {
        const raw = store() && store().getItem(EPS_SESSION_KEY)
        if (!raw) return null
        const s = JSON.parse(raw)
        if (!s || typeof s.token !== 'string' || !s.token) return null
        if (!s.expiresAt || !(new Date(s.expiresAt).getTime() > now)) return null
        return s
    } catch (e) {
        return null
    }
}

export const setEpsSession = ({ token, expiresAt, partner }) => {
    try {
        store().setItem(EPS_SESSION_KEY, JSON.stringify({
            token, expiresAt,
            partnerId: (partner && partner.partnerId) || '',
            partnerName: (partner && partner.partnerName) || '',
        }))
    } catch (e) { /* storage blocked */ }
}

export const clearEpsSession = () => {
    try { store().removeItem(EPS_SESSION_KEY) } catch (e) { /* storage blocked */ }
}

const cleanPath = (pathname) => String(pathname || '/').replace(/\/+$/, '') || '/'

export const isEpsPath = (pathname) => EPS_PATHS.includes(cleanPath(pathname))

/**
 * Where to send the browser, or null to stay.
 * - an /eps-* page other than the login, with no session  -> the portal login
 * - the portal login with a session and no new link code   -> the account page
 * - a portal session on any page outside the portal         -> its account page
 *   (the extranet's own /login stays reachable, to switch account)
 */
export const epsPortalRedirectFor = (pathname, search = '', now = Date.now()) => {
    const p = cleanPath(pathname)
    const session = getEpsSession(now)
    if (isEpsPath(p)) {
        if (p !== EPS_LOGIN && !session) return EPS_LOGIN
        if (p === EPS_LOGIN && session && !codeFromSearch(search)) return EPS_HOME
        return null
    }
    if (session && p !== '/login') return EPS_HOME
    return null
}

/** "abcd…wxyz" — enough to recognise a token without showing it. */
export const maskToken = (token) => {
    const t = String(token || '')
    if (t.length <= 8) return '•'.repeat(t.length)
    return `${t.slice(0, 4)}${'•'.repeat(12)}${t.slice(-4)}`
}

/** The sign-in code in a /eps-login?code=… address, or null. */
export const codeFromSearch = (search) => {
    const m = String(search || '').match(/[?&]code=([A-Za-z0-9_-]{43})(?:&|$)/)
    return m ? m[1] : null
}
